// Pure shaping for the scorecard results modal: one scorecard on one
// applicant, every evaluator's submission side by side.

import scoreCardTemplatesMessages from 'containers/ScoreCardTemplates/messages';
import globalMessages from 'shared/global/messages';

import { messages as formMessages } from '../ScorecardForm/messages';
import {
  ScorecardAnswer,
  ScorecardQuestion,
} from '../ScorecardForm/scorecardFillForm';

export type RecommendationKey = 'strong_no' | 'no' | 'yes' | 'strong_yes';

export interface ScorecardEvaluation {
  // The evaluator's external id.
  user: { id: string | null; name: string | null };
  current_user: boolean; // eslint-disable-line camelcase
  // `pending` covers an evaluator with a draft — their answers never arrive.
  status: 'submitted' | 'pending';
  submitted_at: string | null; // eslint-disable-line camelcase
  invited_at: string | null; // eslint-disable-line camelcase
  // The mode the evaluator submitted under, from the answer snapshots; a
  // template retyped later must not restyle a historical submission.
  submitted_template_type: 'unweighted' | 'weighted' | 'text' | null; // eslint-disable-line camelcase
  score: number | null;
  recommendation: RecommendationKey | null;
  answers: ScorecardAnswer[];
}

export interface ScorecardResultsData {
  template: {
    id: number;
    name: string;
    template_type: 'unweighted' | 'weighted' | 'text'; // eslint-disable-line camelcase
    questions: ScorecardQuestion[];
  };
  applicant: { id: string; name: string };
  evaluations: ScorecardEvaluation[];
}

export const QUESTION_TYPE_LABELS = {
  rating: scoreCardTemplatesMessages.typeRating,
  yes_no: scoreCardTemplatesMessages.typeYesNo, // eslint-disable-line camelcase
  dropdown: scoreCardTemplatesMessages.typeDropdown,
  free_text: scoreCardTemplatesMessages.typeFreeText, // eslint-disable-line camelcase
  recommendation: scoreCardTemplatesMessages.typeRecommendation,
} as const;

export const RECOMMENDATION_LABELS = {
  strong_no: formMessages.recommendationStrongNo, // eslint-disable-line camelcase
  no: globalMessages.no,
  yes: globalMessages.yes,
  strong_yes: formMessages.recommendationStrongYes, // eslint-disable-line camelcase
} as const;

export const isPositiveRecommendation = (key: RecommendationKey): boolean =>
  key === 'yes' || key === 'strong_yes';

// The comparison rows: every question except the recommendation, which gets
// its own summary row under the scores.
export const comparisonQuestions = (
  questions: ScorecardQuestion[],
): ScorecardQuestion[] =>
  questions.filter(question => question.question_type !== 'recommendation');

export const answersByQuestionId = (
  evaluation: ScorecardEvaluation,
): Map<number, ScorecardAnswer> =>
  new Map(evaluation.answers.map(answer => [answer.question_id, answer]));

// What one cell shows. `value` is the submitted answer; `note` is the
// evaluator's comment under it. A free-text answer reads as a note, and an
// answer holding only a note still shows it.
export type AnswerDisplay =
  | { kind: 'none' }
  | { kind: 'value'; value: string; note: string | null }
  | { kind: 'yesNo'; value: 'yes' | 'no'; note: string | null }
  | { kind: 'note'; note: string };

const noteOrNone = (note: string | null): AnswerDisplay =>
  note ? { kind: 'note', note } : { kind: 'none' };

export const answerDisplay = (
  question: ScorecardQuestion,
  answer: ScorecardAnswer | undefined,
): AnswerDisplay => {
  if (!answer) return { kind: 'none' };
  const note = answer.note?.trim() ? answer.note.trim() : null;

  switch (question.question_type) {
    case 'rating':
      return answer.score === null || answer.score === undefined
        ? noteOrNone(note)
        : { kind: 'value', value: String(answer.score), note };
    case 'yes_no':
      return answer.value === 'yes' || answer.value === 'no'
        ? { kind: 'yesNo', value: answer.value, note }
        : noteOrNone(note);
    case 'free_text':
      return answer.value?.trim()
        ? { kind: 'note', note: answer.value.trim() }
        : { kind: 'none' };
    default:
      return answer.value?.trim()
        ? { kind: 'value', value: answer.value, note }
        : noteOrNone(note);
  }
};

// Which score row the summary shows. The live template decides while it can;
// once it is comments-only, the submissions' own modes decide, so a retyped
// template neither hides a stored score nor invents a row with none to show.
export const scoreRowMode = (
  data: ScorecardResultsData,
): 'unweighted' | 'weighted' | null => {
  if (data.template.template_type !== 'text')
    return data.template.template_type;

  const submittedMode = data.evaluations
    .map(evaluation => evaluation.submitted_template_type)
    .find(mode => mode === 'unweighted' || mode === 'weighted');
  return submittedMode ?? null;
};

// A note longer than this renders clamped behind a Show more toggle.
export const NOTE_PREVIEW_LENGTH = 90;

export const notePreview = (note: string): string =>
  `${note.slice(0, NOTE_PREVIEW_LENGTH).trim()}…`;
